import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { replaceAll } from "../../Helper/String";
import { useConfigStore } from "../../Stores/Config";
import { useFormStore } from "../../Stores/Form";
import { useTicketStore } from "../../Stores/Ticket";
import { ReactComponent as XIcon } from "../../Assets/x-mark.svg";
import { ReactComponent as PaperClipIcon } from "../../Assets/paperclip-solid.svg";
import {
  CONVERSATION_ATTACHMENT_LIMIT_BYTES,
  CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE,
  getConversationAttachmentDropzoneErrorMessage,
} from "../../Helper/ConversationAttachmentUpload";
import FormLoading from "../FormLoading/FormLoading";
import { activateOnKey } from "../../Helper/a11y";
import "./UploadFormItem.scss";

function UploadFormItem({ formItem }: { formItem: any }) {
  const ticketStore = useTicketStore();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [uploading, setUploading] = useState(false);

  var accepts: any = {};
  if (formItem.restrictions) {
    const restrictions = replaceAll(formItem.restrictions, " ", "").split(",");
    for (const restriction of restrictions) {
      accepts[restriction] = [];
    }
  }

  const { getRootProps, getInputProps, isDragAccept, isDragReject } =
    useDropzone({
      maxFiles: 1,
      multiple: false,
      accept: accepts,
      maxSize: CONVERSATION_ATTACHMENT_LIMIT_BYTES,
      onDrop: (acceptedFiles) => {
        setErrorMessage(undefined);

        if (acceptedFiles && acceptedFiles.length > 0) {
          setErrorMessage(undefined);
          setUploading(true);

          const file = acceptedFiles[0];
          ticketStore
            .uploadFile(file)
            .then((fileUrl) => {
              setErrorMessage(undefined);
              setUploading(false);
              setFormData(formItem.name, {
                value: fileUrl,
                dirty: true,
                fileName: file.name,
              });
            })
            .catch((err) => {
              setErrorMessage(
                err?.message || CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE
              );
              setUploading(false);
            });
        } else {
          setErrorMessage(CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE);
          setUploading(false);
        }
      },
      onDropRejected: (fileRejections: any[]) => {
        setErrorMessage(
          getConversationAttachmentDropzoneErrorMessage(fileRejections)
        );
        setUploading(false);
      },
    });

  const configStore = useConfigStore();
  const formData = useFormStore((state) => state.formData);
  const fieldFormData = formData[formItem.name];
  const setFormData = useFormStore((state) => state.setFormData);

  const removeFile = () => {
    setFormData(formItem.name, {
      value: undefined,
      dirty: true,
      fileName: undefined,
    });
  };

  const renderDropzone = () => {
    if (uploading) {
      return (
        <div className="upload-form-item-loading">
          <FormLoading />
        </div>
      );
    }

    if (fieldFormData && fieldFormData.value && fieldFormData.fileName) {
      return (
        <div className="file-added-button">
          <PaperClipIcon />
          <span>{fieldFormData.fileName}</span>
          <div
            className="file-added-button-remove"
            role="button"
            tabIndex={0}
            aria-label="Remove file"
            onClick={removeFile}
            onKeyDown={activateOnKey(removeFile)}
          >
            <XIcon />
          </div>
        </div>
      );
    }

    return (
      <div
        {...getRootProps({
          className: `dropzone ${isDragAccept && "dropzone--accept"} ${
            isDragReject && "dropzone--reject"
          }`,
        })}
      >
        <input {...getInputProps({ "aria-label": configStore.translateText("chooseFile") })} />
        <p>{configStore.translateText("chooseFile")}</p>
      </div>
    );
  };

  return (
    <div
      id={formItem.name}
      className="upload-form-item"
      aria-required={formItem.required}
    >
      {renderDropzone()}
      {errorMessage && (
        <p className="upload-form-item-error" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}

export default UploadFormItem;
